import React, { useEffect } from 'react';
import { Box, CircularProgress, Chip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import { fetchMyBrandOpsContract, fetchBrandOpsCalendar } from '../../../redux/brandOps/actions';
import {
    brandOpsMyContractSelector,
    brandOpsFetchingMyContractSelector,
    brandOpsCalendarSelector,
    brandOpsFetchingCalendarSelector,
} from '../../../redux/brandOps/selectors';
import { BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import {
    BrandManageGate,
    ManageGoldButton,
    ManagePageHeader,
    ManageSurface,
    MANAGE_PAGE_SX,
    StatusChip,
    fmtCurrency,
    useBrandManageGate,
} from './manageShared';

const C_TEAL = '#00fdc1';
const C_AMBER = '#ffc965';
const C_ERROR = '#ff7351';
const C_PURPLE = '#b47bff';

function contractStatusTone(status?: string | null): 'mint' | 'gold' | 'error' | 'muted' {
    if (!status) return 'muted';
    if (['active'].includes(status)) return 'mint';
    if (['payment_grace', 'awaiting_signature', 'pending_signature'].includes(status)) return 'gold';
    if (['paused', 'voided', 'void', 'expired'].includes(status)) return 'error';
    return 'muted';
}

function contractStatusLabel(status?: string | null): string {
    if (!status) return 'Unknown';
    return status.replace(/_/g, ' ');
}

function formatCadence(cadence?: string): string {
    if (!cadence) return '—';
    return cadence.replace(/_/g, ' ');
}

function formatFee(fee?: number, currency?: string): string {
    return fmtCurrency(fee, currency);
}

function fmtDate(d?: string | Date | null) {
    if (!d) return '—';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '—';
    const day = date.getDate();
    const suffix =
        day % 10 === 1 && day !== 11
            ? 'st'
            : day % 10 === 2 && day !== 12
            ? 'nd'
            : day % 10 === 3 && day !== 13
            ? 'rd'
            : 'th';
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${month} ${day}${suffix}, ${date.getFullYear()}`;
}

// ── Stat box ─────────────────────────────────────────────────────────────────

function StatBox({
    icon,
    label,
    value,
    valueColor,
}: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    valueColor?: string;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                px: 2,
                py: 1.75,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: BRAND_LUXE_MUTED }}>
                {icon}
                <Typography
                    sx={{
                        fontSize: 10,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        textTransform: 'uppercase',
                        letterSpacing: '0.07em',
                        color: BRAND_LUXE_MUTED,
                    }}>
                    {label}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontSize: 15,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color: valueColor || '#fff',
                    lineHeight: 1.2,
                }}>
                {value}
            </Typography>
        </Box>
    );
}

// ── Escrow period row (read-only manager view) ────────────────────────────────

function EscrowPeriodRow({ period, index }: { period: any; index: number }) {
    const s = period.status;
    const isLocked = s === 'locked';
    const isPaid = s === 'paid' || s === 'released_to_manager';
    const isFailed = s === 'failed';

    const meta = isPaid
        ? {
              color: C_TEAL,
              bg: 'rgba(0,253,193,0.1)',
              icon: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />,
              label: 'Paid out',
          }
        : isLocked
        ? {
              color: C_AMBER,
              bg: 'rgba(255,201,101,0.1)',
              icon: <LockOutlinedIcon sx={{ fontSize: 13 }} />,
              label: 'Locked',
          }
        : isFailed
        ? {
              color: C_ERROR,
              bg: 'rgba(255,115,81,0.1)',
              icon: <ErrorOutlineIcon sx={{ fontSize: 13 }} />,
              label: 'Failed',
          }
        : s === 'released' || s === 'released_to_brand'
        ? {
              color: C_PURPLE,
              bg: 'rgba(180,123,255,0.1)',
              icon: <UndoOutlinedIcon sx={{ fontSize: 13 }} />,
              label: 'Released',
          }
        : {
              color: BRAND_LUXE_MUTED,
              bg: 'rgba(255,255,255,0.06)',
              icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: 13 }} />,
              label: s?.replace(/_/g, ' ') || 'Pending',
          };

    const amountDisplay =
        period.usd_cents != null
            ? fmtCurrency(period.usd_cents / 100, 'usd')
            : period.amount_cents != null
            ? fmtCurrency(period.amount_cents / 100, period.currency)
            : null;

    return (
        <Box
            sx={{
                borderRadius: 2,
                border: `1px solid ${isLocked ? 'rgba(255,201,101,0.18)' : 'rgba(255,255,255,0.06)'}`,
                bgcolor: isLocked
                    ? 'rgba(255,201,101,0.04)'
                    : isPaid
                    ? 'rgba(0,253,193,0.02)'
                    : 'rgba(255,255,255,0.02)',
                overflow: 'hidden',
            }}>
            <Box sx={{ px: 2, pt: 1.75, pb: 1.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 0.75,
                    }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{ color: '#fff', fontSize: 13, fontFamily: FONT_ENUM.MAIN_MEDIUM, lineHeight: 1.3 }}>
                            {period.period_start && period.period_end
                                ? `${fmtDate(period.period_start)} – ${fmtDate(period.period_end)}`
                                : period.label || `Period ${index + 1}`}
                        </Typography>
                        {period.due_at && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
                                <CalendarTodayOutlinedIcon sx={{ fontSize: 10, color: BRAND_LUXE_MUTED }} />
                                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                    Due {fmtDate(period.due_at)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.1,
                            py: 0.35,
                            borderRadius: 5,
                            bgcolor: meta.bg,
                            border: `1px solid ${meta.color}33`,
                            flexShrink: 0,
                        }}>
                        <Box sx={{ color: meta.color, display: 'flex', alignItems: 'center' }}>{meta.icon}</Box>
                        <Typography
                            sx={{
                                color: meta.color,
                                fontSize: 10.5,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                textTransform: 'capitalize',
                            }}>
                            {meta.label}
                        </Typography>
                    </Box>
                </Box>

                {amountDisplay && (
                    <Typography sx={{ color: C_AMBER, fontSize: 15, fontFamily: FONT_ENUM.MAIN_BOLD, mt: 0.5 }}>
                        {amountDisplay}
                    </Typography>
                )}
            </Box>

            {isPaid && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        bgcolor: 'rgba(0,253,193,0.06)',
                        borderTop: '1px solid rgba(0,253,193,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                    }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 14, color: C_TEAL, flexShrink: 0 }} />
                    <Typography sx={{ color: C_TEAL, fontSize: 12 }}>Payment released to you</Typography>
                </Box>
            )}

            {isFailed && period.failure_reason && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        bgcolor: 'rgba(255,115,81,0.06)',
                        borderTop: '1px solid rgba(255,115,81,0.12)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.75,
                    }}>
                    <ErrorOutlineIcon sx={{ fontSize: 14, color: C_ERROR, flexShrink: 0, mt: 0.15 }} />
                    <Typography sx={{ color: C_ERROR, fontSize: 12 }}>{period.failure_reason}</Typography>
                </Box>
            )}
        </Box>
    );
}

// ── Strike row ────────────────────────────────────────────────────────────────

function StrikeRow({ strike }: { strike: any }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                py: 1.25,
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                '&:last-child': { borderBottom: 'none' },
            }}>
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,115,81,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.15,
                }}>
                <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: C_ERROR }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: '#fff', fontSize: 13, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    {strike.reason || 'Policy violation'}
                </Typography>
                {strike.at && (
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.2 }}>
                        {fmtDate(strike.at)}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BrandManageMyContractPage() {
    const dispatch = useDispatch();
    const { brandId } = useBrandManageGate();
    const contract = useSelector(brandOpsMyContractSelector);
    const fetching = useSelector(brandOpsFetchingMyContractSelector);
    const allCalendar = useSelector(brandOpsCalendarSelector);
    const fetchingCalendar = useSelector(brandOpsFetchingCalendarSelector);

    useEffect(() => {
        if (brandId) {
            dispatch(fetchMyBrandOpsContract({ brandId }));
            dispatch(fetchBrandOpsCalendar({ brandId }));
        }
    }, [dispatch, brandId]);

    const displayStatus = contract?.internal_status || contract?.status;
    const strikes = contract?.strikes?.history || [];
    const strikeCount = contract?.strikes?.count ?? strikes.length;
    const escrowPeriods = contract?.escrow_periods || [];
    const assignedPosts = contract?._id ? allCalendar.filter(item => item.contract === contract._id) : [];

    return (
        <BrandManageGate requireEntitlement={false}>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader
                    title="My Contract"
                    subtitle="Your active content manager agreement and escrow status."
                    back={ROUTES.BRAND_MANAGE}
                />

                {fetching && !contract ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress size={28} sx={{ color: BRAND_LUXE_PRIMARY }} />
                    </Box>
                ) : !contract ? (
                    <ManageSurface sx={{ textAlign: 'center', py: 8 }}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 3,
                                bgcolor: 'rgba(255,255,255,0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                            }}>
                            <DescriptionOutlinedIcon sx={{ fontSize: 26, color: BRAND_LUXE_MUTED }} />
                        </Box>
                        <Typography sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 17, mb: 0.75 }}>
                            No contract yet
                        </Typography>
                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            The brand admin will create and dispatch a contract to you.
                        </Typography>
                    </ManageSurface>
                ) : (
                    <>
                        {/* ── Contract header card ─────────────────────────────── */}
                        <Box
                            sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                bgcolor: '#1a1919',
                                border: '1px solid rgba(255,255,255,0.07)',
                                mb: 2,
                            }}>
                            {/* Gradient header band */}
                            <Box
                                sx={{
                                    px: 2.5,
                                    pt: 2.25,
                                    pb: 2,
                                    background:
                                        'linear-gradient(135deg, rgba(255,201,101,0.1) 0%, rgba(0,253,193,0.05) 100%)',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 2,
                                            bgcolor: 'rgba(255,201,101,0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            mt: 0.25,
                                        }}>
                                        <DescriptionOutlinedIcon sx={{ fontSize: 20, color: C_AMBER }} />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: '#fff',
                                                fontSize: 17,
                                                mb: 0.5,
                                                lineHeight: 1.2,
                                            }}>
                                            {contract.title || 'Content Manager Contract'}
                                        </Typography>
                                        <Box
                                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                                            <StatusChip
                                                label={contractStatusLabel(displayStatus)}
                                                tone={contractStatusTone(displayStatus)}
                                            />
                                            {(contract as any).role && (
                                                <StatusChip
                                                    label={(contract as any).role.replace(/_/g, ' ')}
                                                    tone="muted"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                {displayStatus === 'awaiting_signature' && contract._id && brandId && (
                                    <ManageGoldButton
                                        component={RouterLink}
                                        to={`${ROUTES.BRAND_MANAGE_CONTRACTS}/${contract._id}/sign`}
                                        sx={{ flexShrink: 0 }}>
                                        Sign contract
                                    </ManageGoldButton>
                                )}
                            </Box>

                            {/* Stats grid */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
                                    gap: 1.5,
                                    p: 2,
                                }}>
                                <StatBox
                                    icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 13 }} />}
                                    label="Rate"
                                    value={formatFee(contract.fee, contract.currency)}
                                    valueColor={C_AMBER}
                                />
                                <StatBox
                                    icon={<RepeatOutlinedIcon sx={{ fontSize: 13 }} />}
                                    label="Cadence"
                                    value={formatCadence(contract.cadence)}
                                />
                                <StatBox
                                    icon={<WarningAmberOutlinedIcon sx={{ fontSize: 13 }} />}
                                    label="Strikes"
                                    value={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {strikeCount > 0 ? (
                                                <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: C_ERROR }} />
                                            ) : (
                                                <CheckCircleOutlineIcon sx={{ fontSize: 14, color: C_TEAL }} />
                                            )}
                                            {strikeCount}
                                        </Box>
                                    }
                                    valueColor={strikeCount > 0 ? C_ERROR : C_TEAL}
                                />
                                <StatBox
                                    icon={<AssignmentTurnedInOutlinedIcon sx={{ fontSize: 13 }} />}
                                    label="Approval required"
                                    value={contract.require_brand_approval ? 'Yes' : 'No'}
                                />
                                <StatBox
                                    icon={<TimerOutlinedIcon sx={{ fontSize: 13 }} />}
                                    label="Response SLA"
                                    value={
                                        contract.max_response_minutes != null
                                            ? `${contract.max_response_minutes} min`
                                            : '—'
                                    }
                                />
                            </Box>

                            {/* Deliverables + clearances */}
                            {(contract.deliverables || contract.clearance_scopes?.length) && (
                                <Box
                                    sx={{
                                        px: 2,
                                        pb: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.5,
                                        borderTop: '1px solid rgba(255,255,255,0.05)',
                                        pt: 1.75,
                                    }}>
                                    {contract.deliverables && (
                                        <Box>
                                            <Typography
                                                sx={{
                                                    color: BRAND_LUXE_MUTED,
                                                    fontSize: 10,
                                                    mb: 0.5,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.07em',
                                                }}>
                                                Deliverables
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: 13,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    lineHeight: 1.6,
                                                }}>
                                                {contract.deliverables}
                                            </Typography>
                                        </Box>
                                    )}
                                    {contract.clearance_scopes?.length ? (
                                        <Box>
                                            <Typography
                                                sx={{
                                                    color: BRAND_LUXE_MUTED,
                                                    fontSize: 10,
                                                    mb: 0.75,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.07em',
                                                }}>
                                                Clearances
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                {contract.clearance_scopes.map((s: string) => (
                                                    <StatusChip key={s} label={s.replace(/_/g, ' ')} tone="muted" />
                                                ))}
                                            </Box>
                                        </Box>
                                    ) : null}
                                </Box>
                            )}

                            {/* Paused warning */}
                            {displayStatus === 'paused' && (
                                <Box
                                    sx={{
                                        mx: 2,
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        bgcolor: 'rgba(255,115,81,0.08)',
                                        border: '1px solid rgba(255,115,81,0.2)',
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1.25,
                                    }}>
                                    <PauseCircleOutlineIcon sx={{ fontSize: 18, color: C_ERROR }} />
                                    <Typography
                                        sx={{ color: C_ERROR, fontSize: 13, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                        Contract paused — write actions are locked.
                                        {contract.pause_reason
                                            ? ` Reason: ${contract.pause_reason.replace(/_/g, ' ')}.`
                                            : ''}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* ── Escrow periods ───────────────────────────────────── */}
                        <Box
                            sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                bgcolor: '#1a1919',
                                border: '1px solid rgba(255,255,255,0.07)',
                                mb: 2,
                            }}>
                            <Box
                                sx={{
                                    px: 2.5,
                                    pt: 2.25,
                                    pb: 2,
                                    background:
                                        'linear-gradient(135deg, rgba(255,201,101,0.09) 0%, rgba(0,253,193,0.05) 100%)',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                }}>
                                <Box
                                    sx={{
                                        p: 0.75,
                                        borderRadius: 1.5,
                                        bgcolor: 'rgba(255,201,101,0.15)',
                                        display: 'flex',
                                    }}>
                                    <AccountBalanceWalletOutlinedIcon sx={{ color: C_AMBER, fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: '#fff',
                                            fontSize: 14,
                                            lineHeight: 1.2,
                                        }}>
                                        Escrow periods
                                    </Typography>
                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.15 }}>
                                        Funds lock automatically each period and pay out when the period ends
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ px: 2.5, py: 2 }}>
                                {escrowPeriods.length === 0 ? (
                                    <Box sx={{ py: 4, textAlign: 'center' }}>
                                        <HourglassEmptyOutlinedIcon
                                            sx={{ fontSize: 32, color: 'rgba(255,255,255,0.08)', mb: 1 }}
                                        />
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                                            No escrow periods recorded yet.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                        {escrowPeriods.map((p: any, i: number) => (
                                            <EscrowPeriodRow key={p._id || i} period={p} index={i} />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* ── Assigned posts ───────────────────────────────────── */}
                        <Box
                            sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                bgcolor: '#1a1919',
                                border: '1px solid rgba(255,255,255,0.07)',
                                mb: 2,
                            }}>
                            <Box
                                sx={{
                                    px: 2.5,
                                    pt: 2.25,
                                    pb: 2,
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Box
                                        sx={{
                                            p: 0.75,
                                            borderRadius: 1.5,
                                            bgcolor: 'rgba(255,255,255,0.06)',
                                            display: 'flex',
                                        }}>
                                        <CalendarTodayOutlinedIcon sx={{ color: BRAND_LUXE_MUTED, fontSize: 18 }} />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: '#fff',
                                                fontSize: 14,
                                                lineHeight: 1.2,
                                            }}>
                                            Assigned posts
                                        </Typography>
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.15 }}>
                                            Calendar items linked to this contract
                                        </Typography>
                                    </Box>
                                </Box>
                                {fetchingCalendar && <CircularProgress size={14} sx={{ color: BRAND_LUXE_PRIMARY }} />}
                            </Box>

                            <Box sx={{ px: 2.5, py: 2 }}>
                                {assignedPosts.length === 0 && !fetchingCalendar ? (
                                    <Box sx={{ py: 4, textAlign: 'center' }}>
                                        <CalendarTodayOutlinedIcon
                                            sx={{ fontSize: 32, color: 'rgba(255,255,255,0.08)', mb: 1 }}
                                        />
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                                            No posts assigned yet.
                                        </Typography>
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mt: 0.5 }}>
                                            The brand will schedule posts to your calendar.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                        {assignedPosts.map(post => {
                                            const isGood = post.status === 'approved' || post.status === 'published';
                                            const isBad = post.status === 'rejected' || post.status === 'missed';
                                            return (
                                                <Box
                                                    key={post._id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        py: 1.5,
                                                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                                                        '&:last-child': { borderBottom: 'none' },
                                                        gap: 1.5,
                                                    }}>
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Typography
                                                            sx={{
                                                                color: '#fff',
                                                                fontSize: 13,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                textOverflow: 'ellipsis',
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                            {post.title || post.caption?.slice(0, 60) || 'Untitled'}
                                                        </Typography>
                                                        <Typography
                                                            sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.25 }}>
                                                            {post.platform?.toUpperCase()}
                                                            {post.scheduled_at
                                                                ? ` · ${fmtDate(post.scheduled_at)}`
                                                                : ''}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={post.status}
                                                        size="small"
                                                        sx={{
                                                            flexShrink: 0,
                                                            fontSize: 11,
                                                            height: 22,
                                                            bgcolor: isGood
                                                                ? 'rgba(0,253,193,0.1)'
                                                                : isBad
                                                                ? 'rgba(255,115,81,0.1)'
                                                                : 'rgba(255,255,255,0.06)',
                                                            color: isGood ? C_TEAL : isBad ? C_ERROR : BRAND_LUXE_MUTED,
                                                            border: 'none',
                                                            textTransform: 'capitalize',
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* ── Strike history ───────────────────────────────────── */}
                        {strikes.length > 0 && (
                            <Box
                                sx={{
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    bgcolor: '#1a1919',
                                    border: '1px solid rgba(255,115,81,0.15)',
                                }}>
                                <Box
                                    sx={{
                                        px: 2.5,
                                        pt: 2.25,
                                        pb: 2,
                                        background: 'rgba(255,115,81,0.05)',
                                        borderBottom: '1px solid rgba(255,115,81,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                    }}>
                                    <Box
                                        sx={{
                                            p: 0.75,
                                            borderRadius: 1.5,
                                            bgcolor: 'rgba(255,115,81,0.15)',
                                            display: 'flex',
                                        }}>
                                        <ShieldOutlinedIcon sx={{ color: C_ERROR, fontSize: 18 }} />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: '#fff',
                                                fontSize: 14,
                                                lineHeight: 1.2,
                                            }}>
                                            Strike history ({strikeCount})
                                        </Typography>
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.15 }}>
                                            Excessive strikes may trigger contract review
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ px: 2.5, py: 1.5 }}>
                                    {strikes.map((s: any, i: number) => (
                                        <StrikeRow key={i} strike={s} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </BrandManageGate>
    );
}
